const STATE_KEY = "hydrotools:hydrostack-console:v1";

function esc(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function readState() {
  const fallback = {
    autoRefresh: false,
    refreshMs: 2500,
    selectedSensor: "r2sonic",
    payloadText: "test-packet",
  };
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      ...fallback,
      ...(parsed && typeof parsed === "object" ? parsed : {}),
      refreshMs: Math.max(500, Math.min(15000, Number(parsed?.refreshMs || fallback.refreshMs) || fallback.refreshMs)),
      selectedSensor: ["r2sonic", "posmv", "aml"].includes(String(parsed?.selectedSensor || "").toLowerCase())
        ? String(parsed.selectedSensor).toLowerCase()
        : fallback.selectedSensor,
      payloadText: String(parsed?.payloadText || fallback.payloadText),
      autoRefresh: parsed?.autoRefresh === true,
    };
  } catch {
    return fallback;
  }
}

function writeState(state) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
}

async function requestJson(url, options = {}) {
  const timeoutMs = Math.max(500, Number(options.timeoutMs || 5000) || 5000);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const response = await fetch(url, {
    method: options.method || "GET",
    signal: controller.signal,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    body: options.body == null ? undefined : JSON.stringify(options.body),
  }).finally(() => clearTimeout(timer));
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(payload?.error || `Request failed (${response.status})`));
  return payload;
}

function statusTone(status) {
  return status === "ok" ? "#22c55e" : "#ef4444";
}

function timestampLabel(value) {
  if (!value) return "n/a";
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString() : String(value);
}

function asFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function buildSparkline(values, { width = 520, height = 120 } = {}) {
  if (!Array.isArray(values) || values.length < 2) return "";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const points = values.map((v, i) => {
    const x = (i / Math.max(1, values.length - 1)) * width;
    let y;
    if (span === 0) {
      y = height / 2;
    } else {
      y = height - ((v - min) / span) * height;
      if (y < 1) y = 1;
      if (y > height - 1) y = height - 1;
    }
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return `
    <svg viewBox="0 0 ${width} ${height}" width="100%" height="${height}" preserveAspectRatio="none" style="display:block; background:rgba(15,23,42,.35); border:1px solid rgba(148,163,184,.2); border-radius:8px;">
      <polyline fill="none" stroke="#22d3ee" stroke-width="2" points="${points}"></polyline>
    </svg>
  `;
}

export const hydrostackConsoleTool = {
  id: "hydrostack-console",
  name: "Hydrostack Console",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(el);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  let containerEl = null;
  let state = readState();
  let status = "";
  let pollTimer = null;
  let sequence = 0;
  let snapshot = {
    fetchedAt: "",
    api: null,
    acquisition: null,
    fusion: null,
    fusionTiming: null,
    posmvMotion: null,
    offset: null,
    services: [],
    control: null,
  };
  let logs = [];
  let offsetHistory = [];
  let refreshInFlight = false;
  let lastOffsetSampleKey = "";
  let lastOffsetSonarRecv = "";
  let lastOffsetPosmvRecv = "";

  function pushLog(message, level = "info") {
    logs.unshift({
      ts: new Date().toISOString(),
      level,
      message: String(message || ""),
    });
    logs = logs.slice(0, 100);
  }

  function persist() {
    writeState(state);
  }

  function pullStateFromForm() {
    if (!containerEl) return;
    state.autoRefresh = containerEl.querySelector("#hydrostack-auto-refresh")?.checked !== false;
    state.refreshMs = Math.max(500, Math.min(15000, Number(containerEl.querySelector("#hydrostack-refresh-ms")?.value || 2500) || 2500));
    state.selectedSensor = String(containerEl.querySelector("#hydrostack-sensor")?.value || "r2sonic").trim().toLowerCase();
    state.payloadText = String(containerEl.querySelector("#hydrostack-payload")?.value || "");
    persist();
  }

  function renderCard(title, data, extra = "") {
    const statusText = String(data?.status || "offline");
    const natsText = String(data?.nats || "n/a");
    const packetsIngested = Number.isFinite(Number(data?.packets_ingested)) ? Number(data.packets_ingested).toLocaleString() : "-";
    const packetsObserved = Number.isFinite(Number(data?.packets_observed)) ? Number(data.packets_observed).toLocaleString() : "-";
    return `
      <div class="card" style="padding:10px; min-width:0;">
        <div class="row" style="justify-content:space-between;">
          <div style="font-weight:700;">${esc(title)}</div>
          <div class="small" style="color:${statusTone(statusText)}; font-weight:700;">${esc(statusText.toUpperCase())}</div>
        </div>
        <div class="small" style="margin-top:6px;">NATS: ${esc(natsText)}</div>
        <div class="small" style="margin-top:4px;">Ingested: ${esc(packetsIngested)}</div>
        <div class="small" style="margin-top:2px;">Observed: ${esc(packetsObserved)}</div>
        ${extra}
      </div>
    `;
  }

  function renderOffsetPanel() {
    const history = offsetHistory.map((item) => item.offsetMS);
    const sparkline = buildSparkline(history);
    const latest = offsetHistory.length ? offsetHistory[offsetHistory.length - 1] : null;
    const current = asFiniteNumber(snapshot?.offset?.offsetMS) ?? asFiniteNumber(latest?.offsetMS);
    const min = history.length ? Math.min(...history) : null;
    const max = history.length ? Math.max(...history) : null;
    const basis = snapshot?.offset?.basis || latest?.basis || "n/a";
    const samples = history.length;
    return `
      <details class="toolset-group" open style="margin-top:10px;">
        <summary>Realtime Time Offset</summary>
        <div class="toolset-content">
          <div class="small" style="margin-top:6px;">Offset plotted as <code>posmv_time_seconds - sonar_candidate_time_seconds</code>.</div>
          <div class="small" style="margin-top:4px;">Basis: <code>${esc(basis)}</code> | Samples: ${esc(String(samples))}</div>
          <div class="small" style="margin-top:4px;">Current: <strong>${esc(current == null ? "n/a" : `${current.toFixed(1)} ms`)}</strong> | Min: ${esc(min == null ? "n/a" : `${min.toFixed(1)} ms`)} | Max: ${esc(max == null ? "n/a" : `${max.toFixed(1)} ms`)}</div>
          <div style="margin-top:8px;">${sparkline || '<div class="small">Need at least 2 samples to plot.</div>'}</div>
        </div>
      </details>
    `;
  }

  function computeOffsetSample(fusionTiming, posmvMotion) {
    const posmvSec = asFiniteNumber(posmvMotion?.latest?.time_seconds);
    if (posmvSec == null) return null;
    const cands = fusionTiming?.sensor_time_candidates || {};
    const candidateMap = {
      sensor_time_seconds_mod_week_gps: asFiniteNumber(cands?.sonar_sensor_time_seconds_mod_week_gps_sun0),
      sensor_time_seconds_mod_week_unix: asFiniteNumber(cands?.sonar_sensor_time_seconds_mod_week_unix),
      sensor_time_seconds_direct: asFiniteNumber(cands?.sonar_sensor_time_seconds),
    };
    const preferred = String(fusionTiming?.interpolation_basis || "").trim();
    let basis = "";
    let sonarSec = null;
    if (preferred && candidateMap[preferred] != null) {
      basis = preferred;
      sonarSec = candidateMap[preferred];
    } else {
      let bestAbs = Number.POSITIVE_INFINITY;
      for (const [key, val] of Object.entries(candidateMap)) {
        if (val == null) continue;
        const abs = Math.abs(posmvSec - val);
        if (abs < bestAbs) {
          bestAbs = abs;
          basis = key;
          sonarSec = val;
        }
      }
    }
    if (sonarSec == null) return null;
    const offsetSec = posmvSec - sonarSec;
    const pingNumber = fusionTiming?.latest_ping?.ping_number;
    const sonarRecv = String(fusionTiming?.latest_ping?.received_at_utc || "");
    const posmvRecv = String(posmvMotion?.latest?.received_at_utc || "");
    const sampleKey = `${pingNumber ?? "na"}|${sonarRecv}|${posmvRecv}`;
    return {
      ts: new Date().toISOString(),
      basis,
      posmvSec,
      sonarSec,
      offsetMS: offsetSec * 1000,
      sampleKey,
      sonarRecv,
      posmvRecv,
    };
  }

  function render() {
    if (!containerEl) return;
    const services = Array.isArray(snapshot.services) ? snapshot.services : [];
    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Hydrostack Console</div>
        <div class="small" style="margin-top:6px;">Live health, ingest counters, and simulation packet controls for the local Hydrostack backend.</div>

        <details class="toolset-group" open style="margin-top:10px;">
          <summary>System Status</summary>
          <div class="toolset-content">
            <div class="row" style="gap:8px; flex-wrap:wrap;">
              ${renderCard("API", snapshot.api)}
              ${renderCard("Acquisition", snapshot.acquisition)}
              ${renderCard("Fusion", snapshot.fusion)}
            </div>
            <div class="small" style="margin-top:8px;">Last refresh: ${esc(timestampLabel(snapshot.fetchedAt))}</div>
            <div class="small" style="margin-top:4px;">Services advertised: ${services.map((svc) => esc(`${svc.name} (${svc.url})`)).join(" | ") || "n/a"}</div>
            <div class="small" style="margin-top:4px;">Control: ${esc(snapshot?.control?.active ? `running ${snapshot.control.active.action}` : "idle")}</div>
            <div class="row" style="margin-top:10px; gap:10px; flex-wrap:wrap;">
              <button id="hydrostack-refresh" class="btn">Refresh</button>
              <button id="hydrostack-start-stack" class="btn primary">Start Hydrostack</button>
              <button id="hydrostack-stop-stack" class="btn">Stop Hydrostack</button>
              <label class="small"><input id="hydrostack-auto-refresh" type="checkbox" ${state.autoRefresh ? "checked" : ""}/> Auto-refresh</label>
              <label class="small">Interval (ms)
                <input id="hydrostack-refresh-ms" type="number" min="500" max="15000" step="100" value="${esc(String(state.refreshMs))}" style="width:110px; margin-left:6px;" />
              </label>
            </div>
            <div class="small" style="margin-top:8px;">Last command: ${esc(snapshot?.control?.last ? `${snapshot.control.last.action} ${snapshot.control.last.status} (${snapshot.control.last.code})` : "n/a")}</div>
          </div>
        </details>

        <details class="toolset-group" open style="margin-top:10px;">
          <summary>Simulation Packet</summary>
          <div class="toolset-content">
            <div class="row" style="margin-top:6px; gap:10px; flex-wrap:wrap;">
              <label class="small">Sensor
                <select id="hydrostack-sensor" style="margin-left:6px; width:120px;">
                  <option value="r2sonic" ${state.selectedSensor === "r2sonic" ? "selected" : ""}>R2Sonic</option>
                  <option value="posmv" ${state.selectedSensor === "posmv" ? "selected" : ""}>POS MV</option>
                  <option value="aml" ${state.selectedSensor === "aml" ? "selected" : ""}>AML</option>
                </select>
              </label>
            </div>
            <div class="row" style="margin-top:8px;">
              <input id="hydrostack-payload" type="text" value="${esc(state.payloadText)}" placeholder="payload text" style="flex:1;" />
            </div>
            <div class="row" style="margin-top:8px; gap:8px;">
              <button id="hydrostack-send-packet" class="btn primary">Send Packet</button>
              <button id="hydrostack-send-burst" class="btn">Send 25 Packet Burst</button>
            </div>
          </div>
        </details>

        ${renderOffsetPanel()}

        <details class="toolset-group" open style="margin-top:10px;">
          <summary>Activity Log</summary>
          <div class="toolset-content" style="max-height:220px; overflow:auto;">
            ${logs.length
              ? logs.map((item) => `<div class="small" style="margin-top:4px;"><code>${esc(timestampLabel(item.ts))}</code> [${esc(item.level)}] ${esc(item.message)}</div>`).join("")
              : '<div class="small">No activity yet.</div>'}
          </div>
        </details>

        <div class="small" style="margin-top:10px; color:${status ? "#cbd5e1" : "#94a3b8"};">${esc(status || "Ready.")}</div>
      </div>
    `;
  }

  async function refreshStatus({ quiet = false } = {}) {
    if (refreshInFlight) return;
    refreshInFlight = true;
    try {
      const tasks = [
        requestJson("/api/hydrostack-api/healthz", { timeoutMs: 2500 }),
        requestJson("/api/hydrostack-acq/healthz", { timeoutMs: 2500 }),
        requestJson("/api/hydrostack-fusion/healthz", { timeoutMs: 2500 }),
        requestJson("/api/hydrostack-api/v1/system/services", { timeoutMs: 2500 }),
        requestJson("/api/hydrostack/control/status", { timeoutMs: 2500 }),
        requestJson("/api/hydrostack-fusion/v1/fusion/timing/state", { timeoutMs: 2500 }),
        requestJson("/api/hydrostack-fusion/v1/posmv/motion/state", { timeoutMs: 2500 }),
      ];
      const results = await Promise.allSettled(tasks);
      const api = results[0]?.status === "fulfilled" ? results[0].value : snapshot.api;
      const acquisition = results[1]?.status === "fulfilled" ? results[1].value : snapshot.acquisition;
      const fusion = results[2]?.status === "fulfilled" ? results[2].value : snapshot.fusion;
      const servicesPayload = results[3]?.status === "fulfilled" ? results[3].value : { services: snapshot.services || [] };
      const control = results[4]?.status === "fulfilled" ? results[4].value : snapshot.control;
      const fusionTiming = results[5]?.status === "fulfilled" ? results[5].value : snapshot.fusionTiming;
      const posmvMotion = results[6]?.status === "fulfilled" ? results[6].value : snapshot.posmvMotion;

      const offset = computeOffsetSample(fusionTiming, posmvMotion);
      const hasFreshSonar = Boolean(offset?.sonarRecv) && offset.sonarRecv !== lastOffsetSonarRecv;
      const hasFreshPosmv = Boolean(offset?.posmvRecv) && offset.posmvRecv !== lastOffsetPosmvRecv;
      if (offset && offset.sampleKey && offset.sampleKey !== lastOffsetSampleKey && hasFreshSonar && hasFreshPosmv) {
        offsetHistory.push(offset);
        lastOffsetSampleKey = offset.sampleKey;
        lastOffsetSonarRecv = offset.sonarRecv;
        lastOffsetPosmvRecv = offset.posmvRecv;
        if (offsetHistory.length > 480) {
          offsetHistory = offsetHistory.slice(offsetHistory.length - 480);
        }
      }
      snapshot = {
        fetchedAt: new Date().toISOString(),
        api,
        acquisition,
        fusion,
        fusionTiming,
        posmvMotion,
        offset,
        services: Array.isArray(servicesPayload?.services) ? servicesPayload.services : [],
        control,
      };
      if (!quiet) {
        status = "Hydrostack status refreshed.";
        pushLog("Status refresh complete.");
      }
    } catch (error) {
      status = error?.message || "Failed to refresh Hydrostack status.";
      pushLog(status, "error");
    } finally {
      refreshInFlight = false;
      render();
    }
  }

  async function startHydrostack() {
    try {
      status = "Starting Hydrostack services...";
      render();
      const response = await requestJson("/api/hydrostack/control/start", { method: "POST" });
      const result = response?.result || {};
      const summary = `Start command ${result?.status || "done"} (code ${result?.code ?? "?"}).`;
      pushLog(summary);
      if (Array.isArray(result?.output) && result.output.length) {
        pushLog(result.output[result.output.length - 1]);
      }
      status = "Hydrostack start command completed.";
      await refreshStatus({ quiet: true });
      render();
    } catch (error) {
      status = error?.message || "Failed to start Hydrostack.";
      pushLog(status, "error");
      render();
    }
  }

  async function stopHydrostack() {
    try {
      status = "Stopping Hydrostack services...";
      render();
      const response = await requestJson("/api/hydrostack/control/stop", { method: "POST" });
      const result = response?.result || {};
      const summary = `Stop command ${result?.status || "done"} (code ${result?.code ?? "?"}).`;
      pushLog(summary);
      if (Array.isArray(result?.output) && result.output.length) {
        pushLog(result.output[result.output.length - 1]);
      }
      status = "Hydrostack stop command completed.";
      await refreshStatus({ quiet: true });
      render();
    } catch (error) {
      status = error?.message || "Failed to stop Hydrostack.";
      pushLog(status, "error");
      render();
    }
  }

  async function sendSimPacket() {
    pullStateFromForm();
    const sensor = ["r2sonic", "posmv", "aml"].includes(state.selectedSensor) ? state.selectedSensor : "r2sonic";
    const payloadText = String(state.payloadText || "").trim() || "test-packet";
    const payloadBase64 = btoa(unescape(encodeURIComponent(payloadText)));
    try {
      const response = await requestJson("/api/hydrostack-acq/v1/sim/packet", {
        method: "POST",
        body: {
          sensor,
          payload_base64: payloadBase64,
          sleep_ms: 0,
          source_file: "hydrotools-console",
          sequence,
        },
      });
      sequence += 1;
      status = `Queued ${sensor} packet sequence ${response?.sequence ?? sequence - 1}.`;
      pushLog(status);
      await refreshStatus({ quiet: true });
      render();
    } catch (error) {
      status = error?.message || "Packet send failed.";
      pushLog(status, "error");
      render();
    }
  }

  async function sendBurst() {
    pullStateFromForm();
    for (let i = 0; i < 25; i += 1) {
      await sendSimPacket();
    }
    status = "Sent 25 simulated packets.";
    pushLog(status);
    render();
  }

  function startPolling() {
    stopPolling();
    if (!state.autoRefresh) return;
    pollTimer = setInterval(() => {
      refreshStatus({ quiet: true });
    }, state.refreshMs);
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function onInput() {
    pullStateFromForm();
    startPolling();
  }

  function onClick(event) {
    if (event.target?.closest?.("#hydrostack-refresh")) {
      refreshStatus();
      return;
    }
    if (event.target?.closest?.("#hydrostack-start-stack")) {
      startHydrostack();
      return;
    }
    if (event.target?.closest?.("#hydrostack-stop-stack")) {
      stopHydrostack();
      return;
    }
    if (event.target?.closest?.("#hydrostack-send-packet")) {
      sendSimPacket();
      return;
    }
    if (event.target?.closest?.("#hydrostack-send-burst")) {
      sendBurst();
    }
  }

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("click", onClick);
    }
    containerEl = el;
    render();
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("click", onClick);
    if (state.autoRefresh) refreshStatus({ quiet: true });
    startPolling();
  }

  function destroy() {
    stopPolling();
    if (!containerEl) return;
    containerEl.removeEventListener("input", onInput);
    containerEl.removeEventListener("click", onClick);
    containerEl = null;
  }

  function snapshotState() {
    return {
      version: 1,
      state: { ...state },
    };
  }

  function restore(data) {
    const next = data?.state;
    if (!next || typeof next !== "object") return;
    state = {
      ...state,
      ...next,
      autoRefresh: next.autoRefresh === true,
      refreshMs: Math.max(500, Math.min(15000, Number(next.refreshMs || state.refreshMs) || state.refreshMs)),
      selectedSensor: ["r2sonic", "posmv", "aml"].includes(String(next.selectedSensor || "").toLowerCase())
        ? String(next.selectedSensor).toLowerCase()
        : state.selectedSensor,
      payloadText: String(next.payloadText || state.payloadText || ""),
    };
    persist();
    render();
    startPolling();
  }

  return {
    mount,
    destroy,
    snapshot: snapshotState,
    restore,
  };
}
